import { createSlice } from '@reduxjs/toolkit';

import { SEARCH_PROVIDERS } from '../js/common/constants';
import { createBrandAvatarUrl } from '../js/common/utils';
import { fetchCampaignAssetData } from '../campaign/campaignAsset/campaignAssetReducer';
import { fetchStoreCampaigns } from '../product/productDetailsActions';
import {
  fetchCampaignForStore,
  fetchMultipleStoreDiscountAvailable,
  fetchOrganizationCoupons,
  fetchOrganizationLessons,
  fetchOrganizationStores,
  fetchProductsWithCategories,
  fetchRatingBreakdowns,
  fetchStoreDiscountAvailable,
  fetchTaxonomyNames,
  setTaxonomyNames,
} from './productCategoryActions';

const initialState = {
  fetchPendingOrgStores: false,
  fetchPendingProductCategories: false,
  fetchPendingRatingBreakdowns: false,
  locked: null,
  organizationCoupons: null,
  organizationLessons: null,
  organizationStores: null,
  productCategoriesInfo: null,
  ratingBreakdowns: {},
  storesCampaignContents: {},
  storeDiscountAvailableMap: {},
  taxonomyNames: {},
};

const sortFacetGroups = (facetGroups) =>
  (facetGroups || []).map((group) => ({
    ...group,
    facets: group.facets.slice().sort(({ totalCount: a }, { totalCount: b }) => (b - a)),
  }));

const productCategoryReducer = createSlice({
  name: 'productCategory',
  initialState,
  reducers: null,
  extraReducers: (builder) => {
    builder.addCase(fetchCampaignAssetData.fulfilled, (state, action) => {
      state.campaignHomeLinkUrl = action.payload.data.campaignHomeLinkUrl;
    });

    builder.addCase(fetchCampaignForStore.fulfilled, (state, action) => {
      state.defaultCampaignId = parseInt(action.payload.data.id, 10);
    });

    builder.addCase(fetchMultipleStoreDiscountAvailable.fulfilled, (state, action) => {
      state.storeDiscountAvailableMap = action.payload.data?.error ? {} : action.payload.data;
    });

    builder.addCase(fetchOrganizationCoupons.fulfilled, (state, action) => {
      state.organizationCoupons = action.payload.data;
    });

    builder.addCase(fetchOrganizationLessons.fulfilled, (state, action) => {
      const { providerResults: { [SEARCH_PROVIDERS.MODULE]: results } = {} } = action.payload.data || {};
      if (results?.complete && results.resultType !== 'ERROR') {
        state.organizationLessons = {
          items: results.resultItems,
          total: results.totalResults,
        };
      }
    });

    builder.addCase(fetchOrganizationStores.pending, (state) => {
      state.fetchPendingOrgStores = true;
    });

    builder.addCase(fetchOrganizationStores.fulfilled, (state, action) => {
      const { organization, ...data } = action.payload.data;
      state.fetchPendingOrgStores = false;
      state.organizationStores = {
        ...data,
        organization: organization ? {
          avatar: createBrandAvatarUrl(organization.avatarKey, organization.modifiedTime),
          ...organization,
        } : null,
      };
    });

    builder.addCase(fetchOrganizationStores.rejected, (state) => {
      state.fetchPendingOrgStores = false;
    });

    builder.addCase(fetchProductsWithCategories.pending, (state) => {
      state.fetchPendingProductCategories = true;
    });

    builder.addCase(fetchProductsWithCategories.fulfilled, (state, action) => {
      const { taxonomyNames: { ...taxonomyNames } } = state;
      const { payload: { data: productCategoriesInfo } } = action;

      // keep the preFilters from the first request, and discard any future ones
      const preFilterFacetGroups =
        state.productCategoriesInfo
          ? state.productCategoriesInfo.preFilterFacetGroups
          : productCategoriesInfo?.preFilterFacetGroups;

      // extract any taxonomy facets and stuff their names into the taxonomyNames map
      productCategoriesInfo
        ?.facetGroups
        ?.find(({ filterKey }) => filterKey === 'TAXONOMY')
        ?.facets
        ?.forEach(({ id, name }) => { taxonomyNames[id] = name; });

      if (productCategoriesInfo?.facetGroups) {
        // sort the facets by total count since they come back unsorted
        productCategoriesInfo.facetGroups = sortFacetGroups(productCategoriesInfo.facetGroups);
      }

      state.fetchPendingProductCategories = false;
      state.productCategoriesInfo = {
        ...productCategoriesInfo,
        preFilterFacetGroups,
      };
      state.taxonomyNames = taxonomyNames;
    });

    builder.addCase(fetchProductsWithCategories.rejected, (state) => {
      state.fetchPendingProductCategories = false;
    });

    builder.addCase(fetchRatingBreakdowns.pending, (state) => {
      state.fetchPendingRatingBreakdowns = true;
    });

    builder.addCase(fetchRatingBreakdowns.fulfilled, (state, action) => {
      // Bucket the breakdowns up by their grouping property (productCode)
      const breakdowns = ((action.payload.data || {}).results || [])
        .reduce((obj, breakdown) => ({ ...obj, [breakdown.groupingProperty || '']: breakdown }), {});

      // Shim in any attempted product codes that didn't get returned
      const parameters = action.payload.parameters ||
        (action.payload.data && action.payload.data.parameters); // Will be in data on error
      const { productCodes = [] } = parameters || {};
      productCodes
        .filter((pc) => !breakdowns[pc])
        .forEach((pc) => {
          breakdowns[pc] = {
            averageRating: 0,
            totalReviewCount: 0,
          };
        });

      state.fetchPendingRatingBreakdowns = false;
      state.ratingBreakdowns = {
        ...state.ratingBreakdowns || {},
        ...breakdowns,
      };
    });

    builder.addCase(fetchRatingBreakdowns.rejected, (state) => {
      state.fetchPendingRatingBreakdowns = false;
      state.ratingBreakdowns = state.ratingBreakdowns || {};
    });

    builder.addCase(fetchStoreDiscountAvailable.fulfilled, (state, action) => {
      state.locked = !action.payload.data;
    });

    builder.addCase(fetchStoreCampaigns.fulfilled, (state, action) => {
      state.storesCampaignContents = {
        ...state.storesCampaignContents,
        ...action.payload.data,
      };
    });

    builder.addCase(fetchTaxonomyNames.fulfilled, (state, action) => {
      state.taxonomyNames = {
        ...state.taxonomyNames,
        ...action.payload.data,
      };
    });

    builder.addCase(setTaxonomyNames, (state, action) => {
      state.taxonomyNames = {
        ...state.taxonomyNames,
        ...action.payload,
      };
    });
  },
});

// TODO this is clunky; the data should be stored in a unified place
export const selectCampaignContentForStore = (storeId) => (state) =>
  state.productCategories?.storesCampaignContents?.[storeId] ||
  state.productDetails?.storeCampaigns?.[storeId];

export default productCategoryReducer.reducer;
